import { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useTheme } from './components/ThemeProvider';
import { motion } from 'framer-motion';

export default function Loader({ mode = 'DEV', isInitial = true }) {
    const [loading, setLoading] = useState(true);
    const { markReady } = useTheme();
    const [counter, setCounter] = useState(0);

    useEffect(() => {
        if (!loading) return;
        
        let start = null;
        let pId;
        const targetPercent = 100;
        const duration = isInitial ? 2400 : 1200; // Shorter duration for switching
        
        const step = (timestamp) => {
            if (!start) start = timestamp;
            const progress = timestamp - start;
            
            if (isInitial) {
                // Calculate percentage based on time elapsed
                const percentage = Math.min(Math.floor((progress / duration) * targetPercent), targetPercent);
                setCounter(percentage);
            }
            
            if (progress < duration && (!isInitial || counter < targetPercent)) {
                pId = window.requestAnimationFrame(step);
            } else {
                if (isInitial) {
                    // Ensure it shows 80 exactly before transition
                    setCounter(targetPercent);
                }
                
                // Calculate circle geometry
                const cx = window.innerWidth / 2;
                const cy = window.innerHeight / 2;
                const maxRadius = Math.hypot(Math.max(cx, window.innerWidth - cx), Math.max(cy, window.innerHeight - cy));

                // Set CSS variables for the pure CSS animation
                document.documentElement.style.setProperty('--loader-cx', `${cx}px`);
                document.documentElement.style.setProperty('--loader-cy', `${cy}px`);
                document.documentElement.style.setProperty('--loader-radius', `${maxRadius}px`);

                // Add a class to scope the view transition CSS to just the loader
                document.documentElement.classList.add('loader-transition');

                const reveal = () => {
                    flushSync(() => {
                        setLoading(false);
                        markReady();
                    });
                };

                if (typeof document.startViewTransition === 'function' && !document.hidden) {
                    const transition = document.startViewTransition(reveal);
                    
                    transition.ready.then(() => {
                        const y = window.innerHeight / 2;
                        const maxRadius = Math.hypot(window.innerWidth, window.innerHeight);
                        document.documentElement.animate(
                            {
                                clipPath: [
                                    `circle(0px at 0px ${y}px)`,
                                    `circle(${maxRadius}px at 0px ${y}px)`,
                                ],
                            },
                            {
                                duration: 800,
                                easing: 'ease-in-out',
                                pseudoElement: '::view-transition-new(root)',
                            }
                        );
                    });

                    transition.finished.then(() => {
                        document.documentElement.classList.remove('loader-transition');
                    });
                } else {
                    reveal();
                    document.documentElement.classList.remove('loader-transition');
                }
            }
        };
        
        pId = window.requestAnimationFrame(step);
        return () => window.cancelAnimationFrame(pId);
    }, [loading, markReady]);

    if (!loading) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white dark:bg-[#0a0a0a] overflow-hidden">
            
            {isInitial ? (
                <>
                    {/* Background glowing orb */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/40 dark:bg-orange-500/10 rounded-full blur-[120px] animate-pulse pointer-events-none" />
                    
                    <div className="relative flex flex-col items-center justify-center">
                        {/* Circular Loader */}
                        <div className="relative w-48 h-48 flex items-center justify-center">
                            {/* Outer rotating ring */}
                            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="48"
                                    fill="none"
                                    stroke="rgba(255,149,0,0.1)"
                                    strokeWidth="2"
                                />
                                <circle
                                    cx="50"
                                    cy="50"
                                    r="48"
                                    fill="none"
                                    stroke="#FF9500"
                                    strokeWidth="2"
                                    strokeDasharray="301.59"
                                    strokeDashoffset={301.59 - (301.59 * counter) / 100}
                                    className="transition-all duration-75 ease-linear"
                                    strokeLinecap="round"
                                    filter="drop-shadow(0 0 4px rgba(255,149,0,0.5))"
                                />
                            </svg>

                            {/* Inner glowing element */}
                            <div className="absolute w-32 h-32 bg-gradient-to-tr from-[#FF9500]/10 to-transparent rounded-full border border-[#FF9500]/20 animate-[spin_4s_linear_infinite_reverse]" />
                            
                            {/* Center Text */}
                            <div className="absolute flex flex-col items-center justify-center">
                                <span className="text-4xl font-black text-gray-900 dark:text-white tracking-tighter tabular-nums drop-shadow-[0_0_10px_rgba(255,149,0,0.3)]">
                                    {counter}
                                </span>
                                <span className="text-[10px] text-[#FF9500] font-bold tracking-[0.2em] uppercase mt-1">
                                    Percent
                                </span>
                            </div>
                        </div>

                        {/* Loading Text */}
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className="mt-10 flex flex-col items-center gap-2"
                        >
                            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.3em] uppercase text-gray-500 dark:text-gray-400">
                                <span>Initializing Core</span>
                                <span className="flex gap-1">
                                    <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}>.</motion.span>
                                    <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}>.</motion.span>
                                    <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}>.</motion.span>
                                </span>
                            </div>
                        </motion.div>
                    </div>
                </>
            ) : (
                <>
                    {/* Mode Switching Animation */}
                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px] animate-pulse pointer-events-none ${mode === 'GAMER' ? 'bg-purple-600/50 dark:bg-purple-600/20' : 'bg-orange-500/40 dark:bg-orange-500/15'}`} />
                    
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        className="relative z-10 flex flex-col items-center"
                    >
                        {mode === 'GAMER' ? (
                            <div className="text-pink-500 mb-6 drop-shadow-[0_0_15px_rgba(236,72,153,0.5)]">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16 animate-bounce">
                                    <path d="M21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75zm-11.25 1.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm4.5-3a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                </svg>
                            </div>
                        ) : (
                            <div className="text-orange-500 mb-6 font-mono text-6xl font-black animate-pulse drop-shadow-[0_0_15px_rgba(249,115,22,0.5)]">
                                {'</>'}
                            </div>
                        )}

                        <div className={`text-4xl md:text-6xl font-black tracking-tighter uppercase ${mode === 'GAMER' ? 'bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-600' : 'text-gray-900 dark:text-white'}`}>
                            {mode} MODE
                        </div>
                        
                        <div className="mt-6 flex items-center gap-2 text-sm font-bold tracking-[0.3em] uppercase text-gray-500">
                            <span>Initializing Environment</span>
                            <span className="flex gap-1">
                                <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1, repeat: Infinity, delay: 0 }}>.</motion.span>
                                <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}>.</motion.span>
                                <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}>.</motion.span>
                            </span>
                        </div>
                    </motion.div>
                </>
            )}

        </div>
    );
}